<template>
  <div ref="ui" class="ui">
    <div
      v-if="
        !elementCount &&
        !noteCount &&
        !branchCount &&
        !jumperCount &&
        !commentCount &&
        canEdit
      "
      class="helper"
    >
      Double click anywhere on the board to create your first element
    </div>
    <div class="bottom-right">
      <div class="ui-group">
        <div
          v-tooltip="{
            content:
              'Fit selection to screen <span class=\'shortcut\'>F</span>',
            html: true,
            placement: 'top',
            delay: delayOptions,
          }"
          class="button"
          :class="{ disabled: !canFit }"
          @click="fitToScreen"
        >
          <svg-icon
            :width="'22'"
            :height="'18'"
            icon="app-icons/fitscreen"
            fill="#db841e"
          />
        </div>
        <div
          v-tooltip="{
            content: 'Fullscreen',
            placement: 'top',
            delay: delayOptions,
          }"
          class="button"
          @click="toggleFullScreen"
        >
          <svg-icon
            :width="'14'"
            :height="'18'"
            icon="app-icons/fullscreen"
            fill="#db841e"
          />
        </div>
        <div
          v-tooltip="{
            content: 'Zoom in <span class=\'shortcut\'>+</span>',
            html: true,
            placement: 'top',
            delay: delayOptions,
          }"
          class="button"
          @click="zoomIn(0.5)"
        >
          <svg-icon
            :width="'14'"
            :height="'18'"
            icon="app-icons/zoom-in"
            fill="#db841e"
          />
        </div>
        <div
          v-tooltip="{
            content: 'Zoom out <span class=\'shortcut\'>-</span>',
            html: true,
            placement: 'top',
            delay: delayOptions,
          }"
          class="button"
          @click="zoomOut(0.5)"
        >
          <svg-icon
            :width="'14'"
            :height="'18'"
            icon="app-icons/zoom-out"
            fill="#db841e"
          />
        </div>
        <div
          v-tooltip="{
            content: 'Zoom to 100% <span class=\'shortcut\'>0</span>',
            html: true,
            placement: 'top',
            delay: delayOptions,
          }"
          class="zoomboard button"
          @click="resetZoom"
        >
          {{ exactScale }}
        </div>
      </div>
      <div>
        <help-dropdown />
      </div>
    </div>

    <div
      v-if="(canEdit || canComment) && (touchDisabled === 'true' || isDesktop)"
      class="bottom-left gap-1"
    >
      <div class="ui-group">
        <BoardToolsNewElement v-if="canEdit" />
        <board-tools-new-jumper v-if="canEdit" />
        <board-tools-new-branch v-if="canEdit" />
        <board-tools-new-note v-if="canEdit" />
        <board-tools-new-comment v-if="canComment" />
      </div>
      <div
        class="ui-group"
        :class="{ 'grab-on': isCursorGrab }"
        style="padding: 0"
      >
        <board-tools-toggle-cursor v-if="canEdit" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { cloneDeep } from 'lodash/fp';
import { supportsTouchEvents } from 'detect-it';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import {
  SYS_PERMISSIONS,
  ARROW_KEY_ANIMATIONS,
  PANZOOM,
  EVENT,
} from '@/constants.js';
import BoardToolsNewElement from './components/BoardToolsNewElement.vue';
import BoardToolsNewJumper from './BoardToolsNewJumper.vue';
import BoardToolsNewBranch from './BoardToolsNewBranch.vue';
import BoardToolsNewNote from './BoardToolsNewNote.vue';
import BoardToolsNewComment from './BoardToolsNewComment.vue';
import BoardToolsToggleCursor from './BoardToolsToggleCursor.vue';
import HelpDropdown from './components/HelpDropdown.vue';
import Viewport from '../../viewport.js';
import Rect from '../../rect.js';
import SvgIcon from '../all/SvgIcon.vue';
import {
  clamp,
  getDimensions,
  transformToFocal,
  coordsToTransform,
} from '../../utils.js';

export default {
  components: {
    SvgIcon,
    'board-tools-new-jumper': BoardToolsNewJumper,
    'board-tools-new-branch': BoardToolsNewBranch,
    'board-tools-new-note': BoardToolsNewNote,
    'help-dropdown': HelpDropdown,
    BoardToolsNewComment,
    BoardToolsToggleCursor,
    BoardToolsNewElement,
  },

  props: {
    panzoom: {
      type: Object,
      default: null,
    },
    isExport: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    const { hasPermission: canComment } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_COMMENT],
      roles: ['editor', 'viewer'],
    });

    return {
      canEdit,
      canComment,
    };
  },

  data() {
    return {
      isEditingProjectName: false,
      isEditingBoardName: false,
      delayOptions: { show: 800, hide: 0 },
      arrowKeysVector: { x: 0, y: 0 },
      nudgeFast: false,
      animateInterval: null,
      nudgeTimeout: null,
    };
  },

  computed: {
    ...mapGetters('project/data/boards', ['getContainedNodes', 'getNode']),
    ...mapGetters('project/config/boards', ['getOpen']),
    ...mapGetters('project/data/elements', {
      getElements: 'getNodes',
      getElementsBoundingRect: 'getBoundingRect',
    }),
    ...mapGetters('project/data/notes', {
      getNotes: 'getNodes',
      getNotesBoundingRect: 'getBoundingRect',
    }),
    ...mapGetters('project/data/jumpers', {
      getJumpers: 'getNodes',
    }),
    ...mapGetters('project/data/branches', {
      getBranches: 'getNodes',
    }),
    ...mapGetters('project/data/comments', {
      getComments: 'getNodes',
    }),
    ...mapGetters('project/config/elements', {
      getElementSelection: 'getSelections',
    }),
    ...mapGetters('project/config/notes', {
      getNoteSelection: 'getSelections',
    }),
    ...mapGetters('project/config/jumpers', {
      getJumperSelection: 'getSelections',
    }),
    ...mapGetters('project/config/branches', {
      getBranchSelection: 'getSelections',
    }),
    ...mapGetters('project/config/comments', {
      getCommentSelection: 'getSelections',
    }),
    ...mapGetters('project/config/comments', ['getShowResolved']),
    ...mapGetters('project/config', ['getIsSelectionEmpty', 'getGrid']),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),
    ...mapGetters('application', ['getExactTransform']),
    ...mapGetters('project/config', ['getCursorMode']),
    ...mapGetters({ bowser: 'getBowser' }),

    isCursorGrab() {
      return this.getCursorMode === PANZOOM.CURSOR_MODE.GRAB;
    },

    exactTransform() {
      return this.getExactTransform;
    },

    exactScale() {
      return `${parseInt(this.exactTransform.scale * 100, 10)}%`;
    },

    boardId() {
      return this.getOpen;
    },

    board() {
      return this.getNode(this.boardId);
    },

    canUndo() {
      return true; // TO FIX
    },

    canRedo() {
      return true; // TO FIX
    },

    canFit() {
      return this.elementCount || this.noteCount;
    },

    elementCount() {
      if (!this.getIsProjectLoaded) {
        return null;
      }
      return this.getContainedNodes(this.boardId, 'elements', 'elements')
        .length;
    },

    noteCount() {
      if (!this.getIsProjectLoaded) {
        return null;
      }
      return this.getContainedNodes(this.boardId, 'notes', 'notes').length;
    },

    jumperCount() {
      if (!this.getIsProjectLoaded) {
        return null;
      }
      return this.getContainedNodes(this.boardId, 'jumpers', 'jumpers').length;
    },

    branchCount() {
      if (!this.getIsProjectLoaded) {
        return null;
      }
      return this.getContainedNodes(this.boardId, 'branches', 'branches')
        .length;
    },

    commentCount() {
      if (!this.getIsProjectLoaded) {
        return null;
      }
      return this.getContainedNodes(this.boardId, 'comments', 'comments')
        .length;
    },

    touchDisabled() {
      return (!supportsTouchEvents).toString(); // data-simplebar-auto-hide expects string
    },

    isDesktop() {
      return this.bowser.desktop;
    },
  },

  mounted() {
    if (!this.isExport) {
      this.registerEvents();
    }
    // We do this for mobile to position ui correctly
    // this.calcUIWidth();
    // window.addEventListener('resize', this.calcUIWidth, true);
  },

  beforeUnmount() {
    if (!this.isExport) {
      this.unregisterEvents();
    }
    // window.removeEventListener('resize', this.calcUIWidth);
  },

  methods: {
    ...mapActions('project/config/boards', ['setOpen']),
    ...mapActions('project/config', ['moveSelections']),
    ...mapActions('project/config/boards', ['setTransform']),
    ...mapMutations('application', ['setExactTransform']),

    hidePopup() {
      this.showPopup = false;
    },

    registerEvents() {
      emitter.on(EVENT.RESETZOOM, this.resetZoom);
      emitter.on(EVENT.ZOOMTONODE, this.zoomToNode);
      emitter.on(EVENT.ZOOMTOBOARD, this.zoomToBoard);
      emitter.on(EVENT.ZOOMIN, this.zoomIn);
      emitter.on(EVENT.ZOOMOUT, this.zoomOut);
      emitter.on(EVENT.FIT_TO_SCREEN, this.fitToScreen);
      emitter.on(EVENT.PANZOOMTOTRANSFORM, this.panzoomToTransform);

      emitter.on(EVENT.ARROW_KEY_STATE_CHANGE, this.arrowKeyStateChange);
    },

    unregisterEvents() {
      emitter.off(EVENT.RESETZOOM, this.resetZoom);
      emitter.off(EVENT.ZOOMTONODE, this.zoomToNode);
      emitter.off(EVENT.ZOOMTOBOARD, this.zoomToBoard);
      emitter.off(EVENT.ZOOMIN, this.zoomIn);
      emitter.off(EVENT.ZOOMOUT, this.zoomOut);
      emitter.off(EVENT.FIT_TO_SCREEN, this.fitToScreen);
      emitter.off(EVENT.PANZOOMTOTRANSFORM, this.panzoomToTransform);

      emitter.off(EVENT.ARROW_KEY_STATE_CHANGE, this.arrowKeyStateChange);
    },

    /**
     * Pan & Zoom to transform object.
     * @param {Object} transform  The transform object containing the target x, y and scale
     * @param {Object} options    Any options we want to pass to panzoom
     */
    panzoomToTransform(transform, options = {}, fromChannel = false) {
      const scale = this.panzoom.getScale();
      const { x: currentX, y: currentY } = this.panzoom.getPan();
      let result = { ...transform };
      if (scale === transform.scale) {
        result = this.panzoom.pan(transform.x, transform.y, {
          ...options,
        });
      } else {
        // Use focal option if we need to pan & zoom on the same time.
        const focal = transformToFocal({
          x: currentX,
          y: currentY,
          scale,
          toX: transform.x,
          toY: transform.y,
          toScale: transform.scale,
        });
        result = this.panzoom.zoom(
          transform.scale,
          {
            focal,
            ...options,
          },
          fromChannel
            ? {
                type: 'ChannelEvent',
              }
            : undefined
        );
      }
      this.setExactTransform({
        transform: {
          x: result.x,
          y: result.y,
          scale: result.scale,
        },
      });
    },

    /**
     * Zoom to coordinates in the current board
     * @param {Number}  x             The X coordinate
     * @param {Number}  y             The Y coordinate
     * @param {boolean} parentCenter  If we want to move our pan to the center point of the parent node. Defaults to true
     * @param {Number}  toScale       The target scale. Defaults to current scale
     */
    zoomToCoords({
      x,
      y,
      parentCenter = true,
      toScale = this.panzoom.getScale(),
      options = {},
    }) {
      const panzoomEl = document.getElementById('panzoom');

      const transform = coordsToTransform({
        x,
        y,
        scale: toScale,
        panzoomEl,
      });
      /// Pan to center of screen
      if (parentCenter) {
        transform.x += panzoomEl.parentElement.clientWidth / toScale / 2;
        transform.y += panzoomEl.parentElement.clientHeight / toScale / 2;
      }
      this.panzoomToTransform(transform, { animate: true, ...options });
    },

    /**
     * Event handler to pan/zoom to a certain coordinate to a board
     * @param {Number}  x             The X coordinate
     * @param {Number}  y             The Y coordinate
     * @param {boolean} parentCenter  If we want to move our pan to the center point of the parent node. Defaults to true
     * @param {string}  boardId       The boardID that we want to pan to
     * @param {Number}  toScale       The target scale
     */
    zoomToBoard({
      x,
      y,
      parentCenter = true,
      boardId = this.getOpen,
      toScale,
      options = {},
    }) {
      if (Number.isNaN(x) || Number.isNaN(y)) return;
      // If we are not in the same board move to the new board
      if (this.getOpen !== boardId) {
        this.setOpen({
          id: boardId,
          apply: true,
        }).then(response => {
          this.zoomToCoords({ x, y, parentCenter, toScale, options });
        });
      } else {
        this.zoomToCoords({ x, y, parentCenter, toScale, options });
      }
    },

    /**
     * Event handler for zooming to a node (elements, notes, jumpers etc)
     * @param {Object}  node          The node we want to zoom to
     * @param {string}  type          The type of the node ('elements', 'notes' etc.)
     * @param {boolean} center        If we want to center our pan to the center point of the node. Defaults to true
     * @param {boolean} parentCenter  If we want to move our pan to the center point of the parent node. Defaults to true
     * @param {Number}  toScale       The target scale
     * @param {string}  boardId       The board ID that contains the node
     */
    zoomToNode({
      node,
      type,
      center = true,
      parentCenter = true,
      toScale,
      boardId,
    }) {
      let x;
      let y;
      if (type === 'comments') {
        x = node.position.x;
        y = node.position.y;
      } else {
        x = node.x;
        y = node.y;
      }

      if (center) {
        switch (type) {
          case 'elements':
          case 'notes':
            x += node.width / 2;
            y += node.height / 2;
            break;
          case 'jumpers':
            x += 200 / 2;
            y += 81 / 2;
            break;
          case 'branches':
            x += 300 / 2;
            y += 41 / 2;
            break;
          case 'comments':
            x += 22 / 2;
            y += 20 / 2;
            break;
          default:
            console.log(`ZoomToNode: Type: ${type} not found`);
            break;
        }
      }
      this.zoomToBoard({ x, y, toScale, boardId, parentCenter });
    },

    /**
     * Zoom in or out from the center of the panzoom-parent
     * @param {Boolean} zoomIn      If this is a zoom in or out
     * @param {Number}  increment   If set, the scale will be incremented/decremented by this value.
     * @param {Number}  toScale     The target scale. If not provided it will zoom based on the initial step
     */
    zoomInOut({ zoomIn = true, increment, toScale }) {
      const dims = getDimensions(document.getElementById('panzoom'));
      const scale = this.panzoom.getScale();
      const { step } = this.panzoom.getOptions();

      if (toScale === undefined) {
        toScale = scale;
        if (!Number.isNaN(increment)) {
          if (zoomIn) {
            toScale *= increment + 1;
          } else {
            toScale /= increment + 1;
          }
        } else if (zoomIn) {
          toScale += step;
        } else {
          toScale -= step;
        }
      }

      const effectiveArea = {
        width:
          dims.parent.width -
          dims.parent.padding.left -
          dims.parent.padding.right -
          dims.parent.border.left -
          dims.parent.border.right,
        height:
          dims.parent.height -
          dims.parent.padding.top -
          dims.parent.padding.bottom -
          dims.parent.border.top -
          dims.parent.border.bottom,
      };

      let clientX = dims.parent.width / 2;
      let clientY = dims.parent.height / 2;

      clientX =
        clientX -
        dims.parent.padding.left -
        dims.parent.border.left -
        dims.elem.margin.left;
      clientY =
        clientY -
        dims.parent.padding.top -
        dims.parent.border.top -
        dims.elem.margin.top;

      clientX -= dims.elem.width / scale / 2;
      clientY -= dims.elem.height / scale / 2;

      const focal = {
        x: (clientX / effectiveArea.width) * (effectiveArea.width * toScale),
        y: (clientY / effectiveArea.height) * (effectiveArea.height * toScale),
      };

      this.panzoom.zoom(toScale, { focal, animate: true });
    },

    /**
     * Event handler for ZoomIn (+) button. Increments by 0.5
     * @param {Number} increment  The value by to increment the zoom. Defaults to 0.5
     */
    zoomIn(increment = PANZOOM.ZOOMINOUT_INCREMENT) {
      this.zoomInOut({ zoomIn: true, increment });
    },

    /**
     * Event handler for ZoomOut (-) button. Decrements by 0.5
     * @param {Number} increment  The value by to decrement the zoom. Defaults to 0.5
     */
    zoomOut(increment = PANZOOM.ZOOMINOUT_INCREMENT) {
      this.zoomInOut({ zoomIn: false, increment });
    },

    /**
     * Event handler for Zoom Reset. Resets the scale to 1.
     */
    resetZoom() {
      this.zoomInOut({ toScale: 1 });
    },

    /**
     * Returns the coordinates and scale needed to fit to screen
     * @param {Boolean} toSelected    If we need to fit only the selected nodes. Defaults to true
     * @param {Boolean} showComments  If we need to consider the comments. Defaults to true
     */
    fitToScreenCoords(toSelected = true, showComments = true) {
      const pzParent = document.getElementById('panzoom-parent');
      const { left, right, top, bottom } = pzParent.getBoundingClientRect();
      const pzParentRect = new Rect(left, right, top, bottom);

      const viewport = new Viewport(pzParentRect);
      let elements;
      let notes;
      let jumpers;
      let branches;
      let comments;

      let selectionExists = true;
      if (toSelected) {
        const selectedElements = this.getElements(this.getElementSelection);
        const selectedNotes = this.getNotes(this.getNoteSelection);
        const selectedJumpers = this.getJumpers(this.getJumperSelection);
        const selectedBranches = this.getBranches(this.getBranchSelection);
        const selectedComments = showComments
          ? this.getComments(this.getCommentSelection)
          : [];
        selectionExists =
          selectedElements.length ||
          selectedNotes.length ||
          selectedJumpers.length ||
          selectedBranches.length ||
          selectedComments.length;
        if (selectionExists) {
          elements = selectedElements;
          notes = selectedNotes;
          jumpers = selectedJumpers;
          branches = selectedBranches;
          comments = selectedComments;
        }
      }

      if (!toSelected || !selectionExists) {
        elements = cloneDeep(this.getElements(this.board.elements));
        notes = cloneDeep(this.getNotes(this.board.notes));
        jumpers = cloneDeep(this.getJumpers(this.board.jumpers));
        branches = cloneDeep(this.getBranches(this.board.branches));
        comments = showComments
          ? cloneDeep(this.getComments(this.board.comments))
          : [];
      }

      if (!this.getShowResolved) {
        comments = comments.filter(comment => !comment.resolved);
      }

      // Calculate width and height for each jumper
      jumpers.forEach(jumper => {
        const jumperElement = document.getElementById(jumper.id);
        jumper.width = jumperElement?.offsetWidth || 0;
        jumper.height = jumperElement?.offsetHeight || 0;
      });

      // Calculate width and height for each branch
      branches.forEach(branch => {
        const branchElement = document.getElementById(branch.id);
        branch.width = branchElement?.offsetWidth || 0;
        branch.height = branchElement?.offsetHeight || 0;
      });

      // Calculate width and height for each comment
      comments.forEach(comment => {
        const commentElement = document.getElementById(comment.id);
        comment.width = commentElement?.offsetWidth || 0;
        comment.height = commentElement?.offsetHeight || 0;
        comment.x = comment.position.x;
        comment.y = comment.position.y;
      });

      const items = [
        ...elements,
        ...notes,
        ...jumpers,
        ...branches,
        ...comments,
      ];
      const margins = this.isExport
        ? PANZOOM.FIT_TO_VIEWPORT_EXPORT_MARGINS
        : PANZOOM.FIT_TO_VIEWPORT_MARGINS;
      const { min, max } = this.isExport
        ? PANZOOM.SCALE_LIMITS_EXPORT
        : PANZOOM.SCALE_LIMITS;
      return viewport.fitContentToViewport(items, margins, min, max);
    },

    /**
     * Event handler for Fit to Screen button.
     */
    fitToScreen(options = {}, toSelected = true, showComments = true) {
      const location = this.fitToScreenCoords(toSelected, showComments);
      this.zoomToBoard({
        x: location.x,
        y: location.y,
        parentCenter: true,
        toScale: location.scale,
        options,
      });
    },

    calcUIWidth() {
      $('.ui').innerWidth($('.panzoom-parent').innerWidth());
    },

    toggleFullScreen() {
      if (
        !document.fullscreenElement && // alternative standard method
        !document.mozFullScreenElement &&
        !document.webkitFullscreenElement &&
        !document.msFullscreenElement
      ) {
        // current working methods
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if (document.documentElement.msRequestFullscreen) {
          document.documentElement.msRequestFullscreen();
        } else if (document.documentElement.mozRequestFullScreen) {
          document.documentElement.mozRequestFullScreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen(
            Element.ALLOW_KEYBOARD_INPUT
          );
        }
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    },

    /**
     * Custom event handler: An arrow key is down or up.
     * @param {Object} vector       An object of the format { x, y } where x, y can be one of [-1, 0, 1]
     * @param {boolean} nudgeFast   A boolean if the user has shift key pressed
     */
    arrowKeyStateChange(vector, nudgeFast) {
      this.arrowKeysVector.x += vector.x;
      this.arrowKeysVector.y += vector.y;
      // Keep the vector normalized
      this.arrowKeysVector.x = clamp(this.arrowKeysVector.x, -1, 1);
      this.arrowKeysVector.y = clamp(this.arrowKeysVector.y, -1, 1);
      this.nudgeFast = nudgeFast;
      const { x, y } = this.arrowKeysVector;
      if (x === 0 && y === 0) {
        console.log('No vector-clearing timeouts, intervals');
        // Stop any animation if no vector
        clearInterval(this.animateInterval);
        this.animateInterval = null;
        clearTimeout(this.nudgeTimeout);
        this.nudgeTimeout = null;
        // Save transform (if panning)
        if (this.getIsSelectionEmpty) {
          // Get panzoom values
          const scale = this.panzoom.getScale();
          const { x: pzX, y: pzY } = this.panzoom.getPan();
          this.setTransform({
            id: this.getOpen,
            transform: {
              scale,
              x: pzX,
              y: pzY,
            },
            apply: true,
          });
        } else {
          // Perform a null move just to save to the server
          this.moveSelections({ x, y, apply: true, save: true });
        }
      } else if (!this.animateInterval) {
        // Determine the animation rate according to the action
        const { PAN_RATE, NUDGE_FIRST_RATE } = ARROW_KEY_ANIMATIONS;
        // Execute immediately at least once
        this.animateKeyState();
        // If nudge first animation happens on different speed
        if (!this.getIsSelectionEmpty) {
          this.nudgeTimeout = setTimeout(
            this.animateKeyState,
            NUDGE_FIRST_RATE
          );
        } else {
          // Start animation if not already animating
          this.animateInterval = setInterval(this.animateKeyState, PAN_RATE);
        }
      }
    },

    /**
     * Animate the current key state as stored in the keys vector.
     * Either pan or nudge, according to selection existance.
     */
    animateKeyState() {
      console.log('Animating key state');
      let { x, y } = this.arrowKeysVector;
      const { PAN_SPEED, NUDGE_RATE, NUDGE_SPEED, NUDGE_FAST_SPEED } =
        ARROW_KEY_ANIMATIONS;
      // In case of panning
      if (this.getIsSelectionEmpty) {
        const panSpeed = -PAN_SPEED;
        x *= panSpeed;
        y *= panSpeed;
        this.panzoom.pan(x, y, {
          relative: true,
          animate: false,
        });
      } else {
        // Case of nudging
        const { snap, size } = this.getGrid;
        const speed = this.nudgeFast ? NUDGE_FAST_SPEED : NUDGE_SPEED;
        const nudgeSpeed = snap ? size : speed;
        x *= nudgeSpeed;
        y *= nudgeSpeed;
        console.log('Calling move selections with x, y:', x, y);
        // Move the selection but do not save on server until movement complete
        this.moveSelections({ x, y, apply: true, save: false });

        // If we are here after the first frame start animating until key up
        if (!this.animateInterval && this.nudgeTimeout) {
          this.animateInterval = setInterval(this.animateKeyState, NUDGE_RATE);
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ui {
  position: absolute;
  top: 0;
  min-height: 100%;
  right: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.helper {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translateX(-50%);
  z-index: 1;
  color: #fff;
  opacity: 0.3;
  pointer-events: none;
  text-align: center;
}

.bottom-left {
  display: flex;
  position: absolute;
  align-items: center;
  gap: 10px;
  bottom: 10px;
  left: 10px;
  box-shadow: 0px 2px 10px 0 rgba(0, 0, 0, 0.15);
}

.bottom-right {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 10px;
  bottom: 10px;
  right: 10px;
}

.ui-group {
  background: rgb(45 50 51 / 85%);
  backdrop-filter: blur(16px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  z-index: 1;
  padding: 0 10px;
  // &:hover {
  //   background: rgba(45, 50, 51, 1);
  // }

  @media (hover: none) {
    .button {
      :deep(svg path) {
        fill: #db841e !important;
      }
    }
  }

  .button,
  .vddl-list {
    color: #db841e;
    font-size: 14px;
    width: 36px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    &.disabled {
      color: #6f6f6f !important;
      pointer-events: none;
    }

    &.zoomboard {
      min-width: 45px;
    }
  }

  &.grab-on {
    background-color: #db841e;
  }
}

.button {
  cursor: pointer;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  &:hover {
    color: #fff;
    :deep(svg path) {
      fill: #fff;
    }
  }
}

.help-button {
  background: rgba(45, 50, 51, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  z-index: 1;
  bottom: 12px;
  right: 10px;
  color: #db841e;
  width: 34px;
  height: 34px;
  cursor: pointer;

  &:hover {
    color: #fff;
  }
}
</style>

<style lang="scss">
.ui * {
  pointer-events: all;
}

.mini-loader {
  position: absolute;
  top: 26px;
  right: 80px;
  z-index: 1;
  cursor: pointer;
}

.tooltip.loading-tip {
  &[data-popper-placement^='left'] {
    margin-right: 15px;
  }
}

.ui .ui-group .button {
  color: #db841e !important;
  font-size: 14px;
  background: transparent;
  :deep(svg path) {
    fill: #db841e !important;
  }

  &.grab {
    cursor: move; /* fallback if grab cursor is unsupported */
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
  }
}
</style>
